import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rewardApi } from '../../api/reward.api';
import { AnalysisConfigResponse } from '../../types/Reward.api.type';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const POLICY_OPTIONS = [
  { value: 'AD_THEN_PERMANENT', label: '광고 1회 → 영구 해제', desc: '현행. 광고 시청 후 해당 설문 분석 영구 열람' },
  { value: 'FIRST_FREE_THEN_AD', label: '첫방문 무료 → 재방문 광고', desc: '첫 열람은 무료, 같은 설문 재열람 시 광고 필요' },
  { value: 'ALWAYS_FREE', label: '항상 무료', desc: '광고 없이 항상 분석 열람 가능' },
];

export const AnalysisConfigPanel = () => {
  const queryClient = useQueryClient();

  const { data: config, isLoading } = useQuery<AnalysisConfigResponse>({
    queryKey: ['analysisConfig'],
    queryFn: () => rewardApi.getAnalysisConfig().then(res => res.data.results),
  });

  const updateMutation = useMutation({
    mutationFn: (params: { analysisPolicy?: string; aiEnabled?: boolean; pieceCost?: number }) =>
      rewardApi.updateAnalysisConfig(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analysisConfig'] });
    },
  });

  if (isLoading || !config) {
    return <div className="text-center py-8 text-gray-500">로딩 중...</div>;
  }

  return (
    <div className="space-y-6">
      {/* 분석 해제 정책 */}
      <Card>
        <div className="p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">분석 해제 정책</h3>
          <div className="space-y-3">
            {POLICY_OPTIONS.map(opt => (
              <label
                key={opt.value}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  config.analysisPolicy === opt.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
                onClick={() => {
                  if (config.analysisPolicy !== opt.value) {
                    updateMutation.mutate({ analysisPolicy: opt.value });
                  }
                }}
              >
                <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                  config.analysisPolicy === opt.value
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300'
                }`}>
                  {config.analysisPolicy === opt.value && (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="w-1.5 h-1.5 bg-white rounded-full" />
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    {opt.label}
                    {opt.value === 'AD_THEN_PERMANENT' && (
                      <Badge variant="outline" className="text-xs">현행</Badge>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">{opt.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </Card>

      {/* AI 분석 (Haiku) */}
      <Card>
        <div className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-700">AI 분석 (Haiku)</h3>
              <p className="text-xs text-gray-500 mt-1">설문 인사이트 + 투표 성향 AI 생성 텍스트 (약 3원/건)</p>
            </div>
            <Button
              variant={config.aiEnabled ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateMutation.mutate({ aiEnabled: !config.aiEnabled })}
              disabled={updateMutation.isPending}
            >
              {config.aiEnabled ? 'ON' : 'OFF'}
            </Button>
          </div>
          {config.aiEnabled && (
            <div className="mt-3 p-2 bg-green-50 rounded text-xs text-green-700">
              AI 분석이 활성화되어 있어요. 사용자가 분석을 볼 때 Haiku API가 호출됩니다.
            </div>
          )}
          {!config.aiEnabled && (
            <div className="mt-3 p-2 bg-gray-50 rounded text-xs text-gray-500">
              AI 분석이 비활성화되어 있어요. 규칙 기반 분석만 표시됩니다.
            </div>
          )}
        </div>
      </Card>

      {/* 조각 소모 비용 (추후용) */}
      <Card>
        <div className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-700">조각 소모 비용</h3>
              <p className="text-xs text-gray-500 mt-1">0이면 미적용. 추후 광고 대신 조각으로 분석 해제 시 사용</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-mono text-gray-600">{config.pieceCost}조각</span>
              <Badge variant="outline" className="text-xs">추후</Badge>
            </div>
          </div>
        </div>
      </Card>

      {/* 상태 요약 */}
      <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
        <strong>현재 적용 중:</strong>{' '}
        {POLICY_OPTIONS.find(o => o.value === config.analysisPolicy)?.label ?? config.analysisPolicy}
        {' · '}AI {config.aiEnabled ? '활성' : '비활성'}
        {config.pieceCost > 0 && ` · 조각 ${config.pieceCost}개`}
      </div>
    </div>
  );
};
